import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { useMeetings } from '../hooks';
import type {
  MeetingFilters,
  MeetingStatus,
  MeetingType,
} from '../types';
import {
  MEETING_STATUS_LABELS,
  MEETING_TYPE_LABELS,
} from '../types';

interface Props {
  projectId: number | undefined;
  filters: MeetingFilters;
  onFiltersChange: (filters: MeetingFilters) => void;
}

const STATUS_COLORS: Record<MeetingStatus, string> = {
  SCHEDULED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-slate-100 text-slate-700',
};

const formatWhen = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (mins: number): string => {
  if (!mins || mins <= 0) return '—';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

const fullName = (
  user: { first_name: string; last_name: string; email: string } | null,
): string => {
  if (!user) return '—';
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email;
};

export const MeetingsTable: React.FC<Props> = ({
  projectId,
  filters,
  onFiltersChange,
}) => {
  const navigate = useNavigate();
  const { data, isLoading, isError, isFetching } = useMeetings(projectId, filters);

  const items = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const pageSize = filters.page_size ?? 20;
  const currentPage = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + items.length, totalCount);

  const updateFilters = (next: Partial<MeetingFilters>) => {
    onFiltersChange({ ...filters, ...next });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" data-tour-id="meetings-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by title, agenda, summary, location"
            value={filters.search ?? ''}
            onChange={(event) =>
              updateFilters({
                search: event.target.value || undefined,
                page: 1,
              })
            }
          />
        </div>

        <Select
          value={filters.status ?? 'all'}
          onValueChange={(value) =>
            updateFilters({
              status: value === 'all' ? undefined : (value as MeetingStatus),
              page: 1,
            })
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(Object.keys(MEETING_STATUS_LABELS) as MeetingStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {MEETING_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.meeting_type ?? 'all'}
          onValueChange={(value) =>
            updateFilters({
              meeting_type:
                value === 'all' ? undefined : (value as MeetingType),
              page: 1,
            })
          }
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="All meeting types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All meeting types</SelectItem>
            {(Object.keys(MEETING_TYPE_LABELS) as MeetingType[]).map((t) => (
              <SelectItem key={t} value={t}>
                {MEETING_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border" data-tour-id="meetings-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Organizer</TableHead>
              <TableHead className="text-right">Attendees</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!projectId ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  Select a project to view meetings.
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading meetings...
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-destructive"
                >
                  Failed to load meetings.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  No meetings found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(item.id)}
                >
                  <TableCell className="font-medium">{item.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {MEETING_TYPE_LABELS[item.meeting_type] ||
                      item.meeting_type_display}
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLORS[item.status]}>
                      {MEETING_STATUS_LABELS[item.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatWhen(item.scheduled_at)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDuration(item.duration_minutes)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {item.location || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {fullName(item.organizer)}
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {item.attendee_count}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {projectId && totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-3">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1}–{endIndex} of {totalCount}
              {isFetching ? ' …' : ''}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) =>
                  updateFilters({ page_size: Number(value), page: 1 })
                }
              >
                <SelectTrigger className="h-8 w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => updateFilters({ page: currentPage - 1 })}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => updateFilters({ page: currentPage + 1 })}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
